/**
 * Parseo decimal Argentina-safe (alineado a backend productos/decimal_ar.py).
 *
 * Prioridad:
 * 1. Vacío → null
 * 2. Ambos `.` y `,`: el último es decimal; el otro = miles
 * 3. Solo `,`: siempre decimal (AR: `1,234` → 1.234)
 * 4. Solo `.`: miles AR (`1.234`) o decimal US/Excel (`1234.56`, `23493.797044`)
 * 5. Inválido → throw (nunca coercer a 0)
 */
export function parseArgentineDecimal(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "boolean") {
    throw new Error("booleano no es un número válido");
  }
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) throw new Error("número inválido");
    return raw;
  }

  let s = String(raw).replace(/\$/g, "").replace(/\s/g, "").replace(/\u00a0/g, "").trim();
  if (s === "" || s === "-") return null;
  if (s.endsWith("%")) {
    s = s.slice(0, -1).trim();
    if (!s) return null;
  }

  let negative = false;
  if (s.startsWith("-")) {
    negative = true;
    s = s.slice(1);
    if (!s) return null;
  }
  if (s.startsWith("+")) {
    s = s.slice(1);
    if (!s) return null;
  }

  if (/[^\d.,]/.test(s)) {
    throw new Error(`caracteres inválidos en número: ${String(raw)}`);
  }

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  let normalized: string;

  if (hasComma && hasDot) {
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      const idx = s.lastIndexOf(",");
      const intPart = s.slice(0, idx).replace(/\./g, "");
      const frac = s.slice(idx + 1);
      if (!/^\d+$/.test(intPart) || (frac && !/^\d+$/.test(frac))) {
        throw new Error(`número inválido: ${String(raw)}`);
      }
      normalized = frac ? `${intPart}.${frac}` : intPart;
    } else {
      const idx = s.lastIndexOf(".");
      const intPart = s.slice(0, idx).replace(/,/g, "");
      const frac = s.slice(idx + 1);
      if (!/^\d+$/.test(intPart) || (frac && !/^\d+$/.test(frac))) {
        throw new Error(`número inválido: ${String(raw)}`);
      }
      normalized = frac ? `${intPart}.${frac}` : intPart;
    }
  } else if (hasComma) {
    const parts = s.split(",");
    if (parts.length !== 2 || !/^\d+$/.test(parts[0]) || !/^\d+$/.test(parts[1])) {
      throw new Error(`número inválido: ${String(raw)}`);
    }
    normalized = `${parts[0]}.${parts[1]}`;
  } else if (hasDot) {
    const parts = s.split(".");
    if (parts.some((p) => p === "") || !parts.every((p) => /^\d+$/.test(p))) {
      throw new Error(`número inválido: ${String(raw)}`);
    }
    if (
      parts.length >= 2 &&
      parts.slice(1).every((p) => p.length === 3) &&
      parts[0].length >= 1 &&
      parts[0].length <= 3
    ) {
      normalized = parts.join("");
    } else if (parts.length === 2 && parts[1].length >= 1) {
      // Decimal US/Excel (incluye fórmulas con >3 decimales / float basura)
      normalized = `${parts[0]}.${parts[1]}`;
    } else {
      throw new Error(`número inválido o ambiguo: ${String(raw)}`);
    }
  } else {
    if (!/^\d+$/.test(s)) throw new Error(`número inválido: ${String(raw)}`);
    normalized = s;
  }

  const num = Number(normalized);
  if (!Number.isFinite(num)) throw new Error(`número inválido: ${String(raw)}`);
  return negative ? -num : num;
}

/** Como parseArgentineDecimal pero vacío → 0 (precios/cantidades opcionales en preview). */
export function parseArgentineDecimalOrZero(raw: unknown): number {
  const v = parseArgentineDecimal(raw);
  return v === null ? 0 : v;
}

/**
 * Formato AR para plata en preview (2 decimales; evita float basura tipo `...400003`).
 * Si no es numérico, devuelve el texto original.
 */
export function formatArgentineMoneyPreview(raw: unknown): string {
  if (raw === null || raw === undefined || raw === "") return "";
  try {
    const v = parseArgentineDecimal(raw);
    if (v === null) return String(raw);
    return v.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return String(raw);
  }
}

/**
 * Máscara ARS en vivo mientras tipéa: `.` miles, `,` decimales.
 * Digita → agrega puntos de miles; `,` entra a decimales (máx. 2).
 */
export function formatArgentineMoneyTyping(raw: string): string {
  if (raw == null) return "";
  let s = String(raw).replace(/[^\d,]/g, "");
  if (!s) return "";

  const commaIdx = s.indexOf(",");
  let intDigits: string;
  let fracDigits: string | null = null;
  if (commaIdx >= 0) {
    intDigits = s.slice(0, commaIdx).replace(/\D/g, "");
    fracDigits = s.slice(commaIdx + 1).replace(/\D/g, "").slice(0, 2);
  } else {
    intDigits = s.replace(/\D/g, "");
  }

  intDigits = intDigits.replace(/^0+(?=\d)/, "");
  const withDots = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  if (commaIdx >= 0) {
    return `${withDots},${fracDigits ?? ""}`;
  }
  return withDots;
}

/** Normaliza input AR a número redondeado a 2 decimales (plata) para APIs. */
export function toMoneyNumber(raw: unknown): number | null {
  try {
    const v = parseArgentineDecimal(raw);
    if (v === null) return null;
    return Math.round(v * 100) / 100;
  } catch {
    return null;
  }
}
